// frontend/src/pages/SettingsPage.js
//
// All settings displayed and editable here.
// Zero hardcoded values — every option reads from and writes to SettingsStore.
// Components observe SettingsStore; changes take effect app-wide immediately.

import { useSettings } from '../hooks/useSettings';
import styles from './SettingsPage.module.css';

// ── Sub-components (Revealing Module style — defined above usage) ─────────────

function Toggle({ value, onChange, gold = false }) {
  return (
    <button
      role="switch"
      aria-checked={value}
      className={`${styles.toggle} ${value ? (gold ? styles.toggleOnGold : styles.toggleOn) : ''}`}
      onClick={() => onChange(!value)}
    >
      <span className={styles.toggleThumb} />
    </button>
  );
}

function SegmentedControl({ value, options, onChange }) {
  return (
    <div className={styles.segmented}>
      {options.map(opt => (
        <button
          key={opt.value}
          className={`${styles.segOption} ${value === opt.value ? styles.segActive : ''}`}
          onClick={() => onChange(opt.value)}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}

function ColorSwatch({ color, active, onClick, title }) {
  return (
    <button
      className={`${styles.swatch} ${active ? styles.swatchActive : ''}`}
      style={{ background: color }}
      onClick={onClick}
      title={title}
      aria-label={title}
    />
  );
}

function GroupLabel({ children }) {
  return <div className={styles.groupLabel}>{children}</div>;
}

function SettingRow({ label, sub, children }) {
  return (
    <div className={styles.row}>
      <div className={styles.rowLeft}>
        <span className={styles.rowLabel}>{label}</span>
        {sub && <span className={styles.rowSub}>{sub}</span>}
      </div>
      <div className={styles.rowRight}>{children}</div>
    </div>
  );
}

function SelectRow({ label, sub, value, options, settingKey, onChange }) {
  return (
    <div className={styles.row}>
      <div className={styles.rowLeft}>
        <span className={styles.rowLabel}>{label}</span>
        {sub && <span className={styles.rowSub}>{sub}</span>}
      </div>
      <div className={styles.rowRight}>
        <select
          className={styles.select}
          value={value}
          onChange={e => onChange(settingKey, e.target.value)}
        >
          {options.map(o => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      </div>
    </div>
  );
}

function NumberRow({ label, sub, value, settingKey, min, max, step, onChange }) {
  return (
    <div className={styles.row}>
      <div className={styles.rowLeft}>
        <span className={styles.rowLabel}>{label}</span>
        {sub && <span className={styles.rowSub}>{sub}</span>}
      </div>
      <div className={styles.rowRight}>
        <div className={styles.stepper}>
          <button
            className={styles.stepBtn}
            onClick={() => onChange(settingKey, Math.max(min, value - step))}
          >−</button>
          <span className={styles.stepVal}>{value}</span>
          <button
            className={styles.stepBtn}
            onClick={() => onChange(settingKey, Math.min(max, value + step))}
          >+</button>
        </div>
      </div>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function SettingsPage() {
  const { settings, set, reset } = useSettings();

  const ACCENT_OPTIONS = [
    { value: 'gold',  color: '#B8862A', title: 'Temple Gold' },
    { value: 'blue',  color: '#1B4068', title: 'Manuscript Blue' },
    { value: 'red',   color: '#8B1A1A', title: 'Sindoor Red' },
    { value: 'green', color: '#1A4A2E', title: 'Forest Green' },
  ];

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <h1 className={styles.title}>Settings</h1>
        <p className={styles.subtitle}>All preferences. Everything configurable.</p>
      </header>

      {/* ── APPEARANCE ── */}
      <section className={styles.group}>
        <GroupLabel>APPEARANCE</GroupLabel>

        <SettingRow label="Theme" sub="Light, Dark, or follow system">
          <SegmentedControl
            value={settings.theme}
            onChange={v => set('theme', v)}
            options={[
              { value: 'dark',   label: 'Dark' },
              { value: 'light',  label: 'Light' },
              { value: 'system', label: 'System' },
            ]}
          />
        </SettingRow>

        <SettingRow label="Accent Color" sub="Highlights, badges, active states">
          <div className={styles.swatches}>
            {ACCENT_OPTIONS.map(opt => (
              <ColorSwatch
                key={opt.value}
                color={opt.color}
                active={settings.accentColor === opt.value}
                onClick={() => set('accentColor', opt.value)}
                title={opt.title}
              />
            ))}
          </div>
        </SettingRow>

        <SelectRow
          label="Font Size"
          sub="Affects Devanagari and Telugu rendering"
          value={settings.fontSize}
          settingKey="fontSize"
          onChange={set}
          options={[
            { value: 'small',  label: 'Small' },
            { value: 'medium', label: 'Medium' },
            { value: 'large',  label: 'Large' },
          ]}
        />

        <SelectRow
          label="Default Script"
          sub="Primary display for verse text"
          value={settings.defaultScript}
          settingKey="defaultScript"
          onChange={set}
          options={[
            { value: 'devanagari', label: 'देवनागरी (Devanagari)' },
            { value: 'iast',       label: 'IAST Transliteration' },
            { value: 'telugu',     label: 'తెలుగు (Telugu)' },
          ]}
        />
      </section>

      {/* ── SEARCH BEHAVIOUR ── */}
      <section className={styles.group}>
        <GroupLabel>SEARCH BEHAVIOUR</GroupLabel>

        <SelectRow
          label="Default Language"
          sub="Auto-detect or override"
          value={settings.defaultLanguage}
          settingKey="defaultLanguage"
          onChange={set}
          options={[
            { value: 'auto',     label: 'Auto-detect' },
            { value: 'sanskrit', label: 'Sanskrit' },
            { value: 'telugu',   label: 'Telugu' },
            { value: 'english',  label: 'English' },
          ]}
        />

        <SelectRow
          label="Default Tradition"
          sub="Filter applied to every search"
          value={settings.defaultTradition}
          settingKey="defaultTradition"
          onChange={set}
          options={[
            { value: 'all',              label: 'All traditions' },
            { value: 'advaita',          label: 'Advaita' },
            { value: 'dvaita',           label: 'Dvaita' },
            { value: 'vishishtadvaita',  label: 'Viśiṣṭādvaita' },
            { value: 'bhakti',           label: 'Bhakti' },
            { value: 'shaiva',           label: 'Śaiva' },
            { value: 'telugu_kavya',     label: 'Telugu Kavya' },
          ]}
        />

        <NumberRow
          label="Results Per Page"
          value={settings.resultsPerPage}
          settingKey="resultsPerPage"
          min={5} max={50} step={5}
          onChange={set}
        />

        <SettingRow label="Show IAST" sub="Transliteration below verse">
          <Toggle value={settings.showIAST} onChange={v => set('showIAST', v)} />
        </SettingRow>

        <SettingRow label="Show Semantic Score" sub="Relevance badge on results">
          <Toggle value={settings.showSemanticScore} onChange={v => set('showSemanticScore', v)} />
        </SettingRow>

        <SettingRow label="Show Telugu Script" sub="Parallel Telugu text where available">
          <Toggle value={settings.showTelugu} onChange={v => set('showTelugu', v)} />
        </SettingRow>

        <SettingRow
          label="Cross-Encoder Rerank"
          sub="More accurate but slower (v2 feature)"
        >
          <Toggle value={settings.enableCrossEncoder} onChange={v => set('enableCrossEncoder', v)} />
        </SettingRow>
      </section>

      {/* ── CORPUS ── */}
      <section className={styles.group}>
        <GroupLabel>CORPUS</GroupLabel>

        <SettingRow label="Include Sanskrit Texts">
          <Toggle
            value={settings.includeSanskrit}
            onChange={v => set('includeSanskrit', v)}
            gold
          />
        </SettingRow>

        <SettingRow label="Include Telugu Texts">
          <Toggle
            value={settings.includeTelugu}
            onChange={v => set('includeTelugu', v)}
            gold
          />
        </SettingRow>
      </section>

      {/* ── ADVANCED / DEVELOPER ── */}
      <section className={styles.group}>
        <GroupLabel>ADVANCED</GroupLabel>

        <div className={styles.row}>
          <div className={styles.rowLeft}>
            <span className={styles.rowLabel}>API Endpoint</span>
            <span className={styles.rowSub}>Override for self-hosted instance</span>
          </div>
          <div className={styles.rowRight}>
            <input
              type="text"
              className={styles.textInput}
              value={settings.apiEndpoint}
              placeholder="https://your-api.example.com"
              onChange={e => set('apiEndpoint', e.target.value)}
              spellCheck={false}
            />
          </div>
        </div>

        <NumberRow
          label="FAISS Top-K"
          sub="Candidates before post-filtering"
          value={settings.topK}
          settingKey="topK"
          min={5} max={100} step={5}
          onChange={set}
        />

        <SettingRow label="Debug Mode" sub="Show latency and embedding info">
          <Toggle value={settings.debugMode} onChange={v => set('debugMode', v)} />
        </SettingRow>

        <SettingRow label="Query Logging" sub="Contribute to model improvement">
          <Toggle value={settings.enableQueryLogging} onChange={v => set('enableQueryLogging', v)} />
        </SettingRow>
      </section>

      {/* ── ABOUT ── */}
      <section className={styles.group}>
        <GroupLabel>ABOUT</GroupLabel>
        <div className={`${styles.row} ${styles.rowReadonly}`}>
          <span className={styles.rowLabel}>Corpus Version</span>
          <span className={styles.aboutVal}>v1.0.0</span>
        </div>
        <div className={`${styles.row} ${styles.rowReadonly}`}>
          <span className={styles.rowLabel}>Model</span>
          <span className={`${styles.aboutVal} ${styles.mono}`}>IndicBERT fine-tuned</span>
        </div>
        <div className={`${styles.row} ${styles.rowReadonly}`}>
          <span className={styles.rowLabel}>App Version</span>
          <span className={styles.aboutVal}>1.0.0 (1)</span>
        </div>
      </section>

      {/* ── RESET ── */}
      <section className={styles.resetSection}>
        <button
          className={styles.resetBtn}
          onClick={() => {
            if (window.confirm('Reset all settings to defaults?')) reset();
          }}
        >
          Reset All to Defaults
        </button>
      </section>
    </div>
  );
}
